import * as React from 'react'
import produce from 'immer'
import { Button, Form, Input, Spin, Tabs, Dropdown, Menu, Tooltip, message } from 'antd'
import { cloneDeep, get, isNil, set, isEmpty, max, orderBy, isEqual, remove, unset, flow } from 'lodash'
import { saveAs } from 'file-saver'
import b64toBlob from 'b64-to-blob'
import { withRouter } from 'react-router-dom'
import { HARD_SPACE } from 'helpers/utils'
import { ValidationError, showError, showClientNotifications, showValidationError } from 'helpers/errors'
import { createFieldFactory, createHandleSubmit, createLabelFactory, isReadOnly } from 'helpers/formViews'
import { createPrintHandler, resetPrintHandler } from 'helpers/print'
import { formatUserTime } from 'helpers/dateTime'
import { t } from 'helpers/i18n'
import FormButtons from 'elements/FormButtons'
import Modal from 'elements/Modal'
import SelectFormTemplate from 'containers/FormTemplates/Select'
import Select, { Option, getOptionProps } from 'elements/Select'
import DatePicker from 'elements/DatePicker'
import Checkbox from 'elements/Checkbox'
import Icon from 'elements/Icon'
import { Row, Col } from 'elements/Grid'
import {
  getSectionProps,
  getWidths,
  getLabel,
  isValidAnswer,
  allowHighlight,
  allowAttachment,
  allowRequired,
  extractFormQuestions,
} from 'helpers/formTemplates'
import linkTargets from 'options/linkTargets'
import { Emitter, stopEvent } from 'helpers/events'
import { FORM_INSTANCES_REFRESH, FORM_CHANGE_EVENT } from 'options/events'
import { FormInstancesContext } from 'helpers/formInstances'
import Field from './Field'
import Drawer from 'elements/Drawer'
import FileUpload from 'elements/FileUpload'
import Print from 'containers/FormInstances/Print'
import UnderDevelopment from 'elements/UnderDevelopment'
import { Container, Actions, Section } from './Styled'

class FormView extends React.Component {
  static contextType = FormInstancesContext

  state = {}

  constructor(props) {
    super(props)

    this.printableRef = React.createRef()
    this.handleSubmit = createHandleSubmit(this)
  }

  componentDidMount() {
    const { selectedFormTemplate } = this.props

    if (selectedFormTemplate) {
      this.setState({ selectedFormTemplate }, this.handleSelectFormTemplateOk)
    } else {
      this.fetchItem()
    }

    Emitter.on(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  componentWillUnmount() {
    Emitter.off(FORM_CHANGE_EVENT, this.handleFormChange)
  }

  promiseState = (state = {}) => new Promise((resolve) => this.setState(state, resolve))

  handleFormChange = () => this.setState({ formHasChanged: true })

  saveItemSuccess = () => Emitter.emit(FORM_INSTANCES_REFRESH)

  handleSelectFormTemplateOk = async () => {
    const { objectType = 'User', objectId = 0 } = this.props

    if (this.state.selectedFormTemplate) {
      try {
        this.setState({ selectProductVisible: false })

        const templateId = this.state.selectedFormTemplate.id
        const responses = await Promise.all([
          this.props.newItem({ templateId, objectType, objectId }),
          this.props.getSettings({ type: this.props.settingsType }),
          objectType === 'User' ? this.props.getUsers() : Promise.resolve(null),
          this.props.getFormTemplate(templateId).then((r) => r.value.data),
        ])

        const item = cloneDeep(responses[0].value.data)

        item.formAnswers.forEach((formAnswer) => {
          if (formAnswer.id === 0) {
            unset(formAnswer, 'id')
          }

          if (formAnswer.formInstanceId === 0) {
            unset(formAnswer, 'formInstanceId')
          }
        })

        const formTemplate = responses[3]

        item.formAnswers = this.normalizeFormAnswers(item, formTemplate)

        this.setState({
          item,
          fieldSettings: responses[1].value.data.fieldSettings,
          users: get(responses[2], 'value.data.items', []),
          formTemplate,
          attachments: [],
          attachmentsOriginal: [],
        })
      } catch (error) {
        showError({ error })
      }
    }
  }

  getItemId = () => {
    const targetTenantId = this.context?.targetTenantId ?? 0

    const itemId = [
      this.state.item?.id,
      this.props.linkTargetRecord?.formInstanceId,
      this.props.linkTargetRecord?.id,
    ].find(Boolean)

    if (itemId && this.props.userIsInRoleSupplier) {
      return `${itemId}?targetTenantId=${targetTenantId}`
    }

    return itemId
  }

  fetchItem = async ({ itemId = this.getItemId() } = {}) => {
    const targetTenantId = this.context?.targetTenantId ?? 0

    if (isNil(itemId) && !this.props.linkTargetRecord) {
      this.setState({ selectFormTemplateVisible: true })
    } else {
      try {
        const responses = await Promise.all([
          this.props.getItem(itemId).catch((error) => {
            this.props.onCancel?.()
            throw error
          }),
          this.props.getSettings({ type: this.props.settingsType }),
          this.props.getUsers(),
        ])

        const item = cloneDeep(responses[0].value.data)

        const [formTemplate, attachments] = await Promise.all([
          this.props
            .getFormTemplate(
              this.props.userIsInRoleSupplier
                ? `${item.templateId}?targetTenantId=${targetTenantId}`
                : item.templateId
            )
            .then((r) => r.value.data),
          item.id > 0 ? this.props.getAttachments(item.id, { targetTenantId }) : Promise.resolve(null),
        ])

        item.formAnswers = this.normalizeFormAnswers(item, formTemplate)

        this.setState({
          item,
          itemOriginal: cloneDeep(item),
          fieldSettings: responses[1].value.data.fieldSettings,
          users: get(responses[2], 'value.data.items', []),
          formTemplate,
          attachments: get(attachments, 'value.data.items', []),
          attachmentsOriginal: cloneDeep(get(attachments, 'value.data.items', [])),
          formHasChanged: false,
        })
      } catch (error) {
        showError({ error })
      }
    }
  }

  beforeValidateFields() {
    this.setState({ validationErrors: [], questionErrors: [] })
  }

  saveItem = async (item) => {
    const {
      createItem = (data) => Promise.resolve({ value: { data } }),
      updateItem = (data) => Promise.resolve({ value: { data } }),
    } = this.props

    if (!item) {
      throw new Error('item is undefined')
    }

    const saved = item.id ? await updateItem(item) : await createItem(item)

    await this.saveAttachments(saved.value.data.id)

    return saved
  }

  setItemValue = (name, value) => {
    const { item } = this.state

    this.setState(
      produce((draft) => {
        set(draft.item, name, value)

        this.props.form.setFieldsValue({ [name]: value })
      }),
      async () => {
        if (['deficiency', 'reviewed', 'corrected'].includes(name)) {
          try {
            this.props.onSave?.(true)

            const response = await this.props.updateStatus(item.id, name, value)

            showClientNotifications({ response })
          } catch (error) {
            showError({ error })

            this.props.form.setFieldsValue({ [name]: !value })
          } finally {
            await this.fetchItem()

            Emitter.emit(FORM_INSTANCES_REFRESH)

            this.props.onSave?.(false)
          }
        }
      }
    )
  }

  setAnswerValue = (questionId, value) =>
    this.setState(
      produce((draft) => {
        const formAnswer = draft.item.formAnswers.find((one) => one.questionId === questionId)

        console.assert(formAnswer, `no answer for questionId = ${questionId}`)

        if (formAnswer) {
          Object.assign(formAnswer, value)
        }
      })
    )

  normalizeFormAnswers = (formInstance, formTemplate) =>
    extractFormQuestions(formTemplate).map(
      (question) =>
        formInstance.formAnswers.find((one) => one.questionId === question.id) || {
          formInstanceId: formInstance.id,
          questionId: question.id,
          value: '',
          highlight: false,
          attachmentFileName: '',
        }
    )

  validateFormQuestions = (callback) =>
    extractFormQuestions(this.state.formTemplate)
      .map((formQuestion) => {
        const formAnswer = this.state.item.formAnswers.find((one) => one.questionId === formQuestion.id)

        return !isValidAnswer(formQuestion, formAnswer) ? { [formQuestion.id]: 'error' } : undefined
      })
      .reduce((acc, each) => ({ ...acc, ...each }), {})

  hasUnsavedChanges = () =>
    this.state.formHasChanged ||
    !this.state.item?.id ||
    !isEqual(this.state.item, this.state.itemOriginal) ||
    !isEqual(this.state.attachments, this.state.attachmentsOriginal)

  handleSubmitButtonClick = () => {
    const { item } = this.state

    if (this.hasUnsavedChanges()) {
      message.error(t('saveChangesFirst'))
      return
    }

    const questionErrors = this.validateFormQuestions()

    this.props.form.validateFields((errors, values) => {
      this.setState(
        (draft) => {
          draft.focusedId = null
          draft.validationErrors = errors
          draft.questionErrors = questionErrors
        },
        async () => {
          if (!isEmpty(errors) || !isEmpty(questionErrors)) {
            showValidationError()
            return
          }

          try {
            await this.promiseState({ submitButtonLoading: true })

            await this.props.onSave?.(true)

            const response = await this.props.submitItem(item.id)

            showClientNotifications({ response })

            if (response.value.data.failureCount > 0) {
              throw new Error()
            }

            await this.fetchItem()

            Emitter.emit(FORM_INSTANCES_REFRESH)

            await this.props.onSave?.(false)
          } catch (error) {
            showError({ error })
            await this.props.onSave?.(false)
          } finally {
            this.setState({ submitButtonLoading: false })
          }
        }
      )
    })
  }

  handleActionsMenuClick = async ({ key }) => {
    switch (key) {
      case 'downloadAttachments':
        if (this.hasUnsavedChanges()) {
          message.error(t('saveChangesFirst'))
        } else {
          try {
            await this.props.onSave?.(true)

            const response = await this.props.downloadAttachments(this.state.item.id)

            saveAs(response.value.data, `${t('attachments')}.zip`)
          } catch (error) {
            showError({ error })
          } finally {
            await this.props.onSave?.(false)
          }
        }

        break

      case 'print':
        this.setState(
          produce((draft) => {
            draft.printItem = { ...draft.item, ...this.props.form.getFieldsValue() }
            draft.printVisible = true
          })
        )
        break

      default:
        message.warn(t('underDevelopment'))
        break
    }
  }

  downloadAttachment = async (questionId, fileName) => {
    const { item } = this.state

    const attachment = this.state.attachments.find((one) => one.questionId === questionId)

    if (attachment.document) {
      try {
        await this.props.onSave?.(true)

        const response = await this.props.downloadAttachment(item.id, {
          questionId,
          documentFile: {
            domainObjectId: item.id,
            domainObjectType: 'FormInstance',
            documentType: 'ObjectDocument',
            fileName,
          },
        })

        saveAs(response.value.data, fileName)
      } catch (error) {
        showError({ error })
      } finally {
        await this.props.onSave?.(false)
      }
    } else if (attachment.documentFile) {
      try {
        saveAs(b64toBlob(attachment.documentFile.contents, attachment.documentFile.fileType), fileName)
      } catch (error) {
        showError({ error })
      }
    }
  }

  deleteAttachment = (questionId) =>
    this.setState(
      produce((draft) => {
        remove(draft.attachments, (one) => one.questionId === questionId)
        draft.item.formAnswers.find((one) => one.questionId === questionId).attachmentFileName = ''
      })
    )

  handleFileUpload = (fileList = []) => {
    const { fileName, fileType, fileContents: contents } = fileList[0]

    this.setState(
      produce((draft) => {
        const formAnswer = draft.item.formAnswers.find((one) => one.questionId === draft.fileUploadQuestionId)

        console.assert(formAnswer, `no answer for questionId = ${draft.fileUploadQuestionId}`)

        if (formAnswer) {
          remove(draft.attachments, (one) => one.questionId === draft.fileUploadQuestionId)

          formAnswer.attachmentFileName = fileName

          draft.attachments.push({
            questionId: draft.fileUploadQuestionId,
            documentFile: {
              domainObjectId: draft.item.id,
              domainObjectType: 'FormInstance',
              documentType: 'ObjectDocument',
              fileName,
              fileType,
              contents,
            },
          })
        }

        draft.fileUploadQuestionId = null
        draft.fileUploadDrawerVisible = false
      })
    )
  }

  saveAttachments = async (formInstanceId) => {
    const { item, attachments, attachmentsOriginal } = this.state

    const questionIds = attachmentsOriginal.map((each) => each.questionId)

    const deleting = item.formAnswers
      .filter((each) => isEmpty(each.attachmentFileName) && questionIds.includes(each.questionId))
      .map((each) => each.questionId)

    if (!isEmpty(deleting)) {
      const response = await this.props.deleteAttachments(formInstanceId, [...new Set(deleting)])

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }
    }

    const creating = attachments.filter((each) => isNil(each.document) && !isNil(each.documentFile))

    if (!isEmpty(creating)) {
      const response = await this.props.createAttachments(
        formInstanceId,
        creating.map((each) =>
          produce(each, (draft) => {
            draft.documentFile.domainObjectId = formInstanceId
          })
        )
      )

      showClientNotifications({ response })

      if (response.value.data.failureCount > 0) {
        throw new Error()
      }
    }
  }

  handleSourceClick = () =>
    this.setState(
      produce((draft) => {
        try {
          const { objectType = '', objectId, source } = draft.item
          const sourceDrawerLinkTarget = get(linkTargets, `${objectType.toLowerCase()}Record`)

          draft.sourceDrawerTitle = source
          draft.sourceDrawerSize = sourceDrawerLinkTarget.formSize
          draft.sourceDrawerFormView = sourceDrawerLinkTarget.formComponent
          draft.sourceDrawerLinkTargetRecord = { objectId }
          draft.sourceDrawerVisible = true
        } catch (error) {
          showError({ error })
        }
      })
    )

  render() {
    const { onCancel, tenantTypeIsDcrib, userIsDcribDistributor } = this.props
    const { item, fieldSettings, formTemplate, validationErrors, questionErrors } = this.state

    if (!item || !fieldSettings) {
      return (
        <>
          <Spin />
          <Modal
            title={t('selectFormTemplate')}
            visible={this.state.selectFormTemplateVisible}
            okText={t('create')}
            onOk={this.handleSelectFormTemplateOk}
            okButtonProps={{ disabled: !this.state.selectedFormTemplate }}
            onCancel={onCancel}
            width={992}
          >
            <SelectFormTemplate onSelect={(values) => this.setState({ selectedFormTemplate: values[0] })} />
          </Modal>
        </>
      )
    }

    const createLabel = createLabelFactory(fieldSettings)
    const createFieldDecorator = createFieldFactory(this.props.form, item, fieldSettings)

    const readOnly = isReadOnly(this) || item.done || userIsDcribDistributor
    const SourceComponent = get(this.state, 'sourceDrawerFormView', UnderDevelopment)

    return (
      <>
        <Form layout="vertical" colon={false} onSubmit={readOnly ? stopEvent : this.handleSubmit}>
          {item.id ? (
            <Dropdown
              overlay={
                <Menu onClick={this.handleActionsMenuClick}>
                  <Menu.Item key="downloadAttachments">{t('downloadAttachments')}</Menu.Item>
                  <Menu.Divider />
                  <Menu.Item key="print">{t('print...')}</Menu.Item>
                </Menu>
              }
              trigger={['click']}
            >
              <Button className="form-items-actions">
                {t('actions')}
                <Icon type="KeyboardArrowDown" />
              </Button>
            </Dropdown>
          ) : null}
          <div className="form-items-container">
            <ValidationError errors={{ ...validationErrors, ...questionErrors }} />
            <Row>
              <Col xs={24} md={8}>
                <Form.Item label={createLabel('name')}>
                  {createFieldDecorator('name')(
                    <Input
                      onChange={(e) => this.setItemValue('name', e.target.value)}
                      readOnly={readOnly}
                      autoFocus
                    />
                  )}
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label={createLabel('formTemplateName')}>
                  {createFieldDecorator('formTemplateName')(<Input disabled />)}
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label={createLabel('done')}>
                  <Input
                    value={item.done ? formatUserTime(item.lastSubmitDate, item.lastUpdateBy) : null}
                    disabled
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col xs={24} md={16}>
                <Form.Item label={createLabel('formTemplateDescription')}>
                  {createFieldDecorator('formTemplateDescription')(<Input disabled />)}
                </Form.Item>
                <Row>
                  <Col xs={24} md={12}>
                    {item.objectType === 'User' ? (
                      <Form.Item label={createLabel('userName')}>
                        {createFieldDecorator('userName')(
                          <Select onChange={(value) => this.setItemValue('name', value)} readOnly={readOnly}>
                            <Option value="">{t('none')}</Option>
                            {(this.state.users ?? []).map((each) => (
                              <Option key={each.userName} value={each.userName}>
                                <span {...getOptionProps(each)}>{each.displayName}</span>
                              </Option>
                            ))}
                          </Select>
                        )}
                      </Form.Item>
                    ) : (
                      <Form.Item label={t('source')}>
                        <div className="ant-input ant-input-disabled">
                          <a onClick={this.handleSourceClick}>{item.source}</a>
                        </div>
                      </Form.Item>
                    )}
                  </Col>
                  <Col xs={24} md={12}>
                    <Form.Item label={createLabel('dueDate')}>
                      {createFieldDecorator('dueDate')(
                        <DatePicker
                          onChange={(value) => this.setItemValue('dueDate', value)}
                          readOnly={readOnly}
                        />
                      )}
                    </Form.Item>
                  </Col>
                </Row>
              </Col>
              <Col xs={24} md={8}>
                {tenantTypeIsDcrib ? (
                  <>
                    <Row>
                      <Col half>
                        <Form.Item className="mb-0" help={HARD_SPACE}>
                          {createFieldDecorator('required', { valuePropName: 'checked' })(
                            <Checkbox
                              onChange={(e) => this.setItemValue('dueDate', e.target.checked)}
                              readOnly={readOnly}
                            >
                              {createLabel('required')}
                            </Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                      <Col half>
                        <Form.Item className="mb-0" help={HARD_SPACE}>
                          {createFieldDecorator('deficiency', { valuePropName: 'checked' })(
                            <Checkbox
                              onChange={(e) => this.setItemValue('deficiency', e.target.checked)}
                              disabled={
                                isNil(item.id) || item.id < 0 || !item.done || !userIsDcribDistributor
                              }
                            >
                              {createLabel('deficiency')}
                            </Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                    <Row>
                      <Col xs={24} sm={12} md={24}>
                        <Form.Item
                          className="mb-0"
                          help={
                            item.reviewed
                              ? formatUserTime(item.reviewDate, item.reviewBy) || HARD_SPACE
                              : HARD_SPACE
                          }
                        >
                          {createFieldDecorator('reviewed', { valuePropName: 'checked' })(
                            <Checkbox
                              onChange={(e) => this.setItemValue('reviewed', e.target.checked)}
                              disabled={
                                isNil(item.id) || item.id < 0 || !item.done || !userIsDcribDistributor
                              }
                            >
                              {createLabel('reviewed')}
                            </Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                      <Col xs={24} sm={12} md={24}>
                        <Form.Item
                          className="mb-0"
                          help={
                            item.corrected
                              ? formatUserTime(item.correctDate, item.correctBy) || HARD_SPACE
                              : HARD_SPACE
                          }
                        >
                          {createFieldDecorator('corrected', { valuePropName: 'checked' })(
                            <Checkbox
                              onChange={(e) => this.setItemValue('corrected', e.target.checked)}
                              disabled={!(item.id > 0) || !item.done}
                            >
                              {createLabel('corrected')}
                            </Checkbox>
                          )}
                        </Form.Item>
                      </Col>
                    </Row>
                  </>
                ) : (
                  <Form.Item label={HARD_SPACE}>
                    {createFieldDecorator('required', { valuePropName: 'checked' })(
                      <Checkbox readOnly={readOnly}>{createLabel('required')}</Checkbox>
                    )}
                  </Form.Item>
                )}
              </Col>
            </Row>
            <Container>
              <Tabs>
                {(formTemplate?.formPages ?? []).map((formPage) => (
                  <Tabs.TabPane key={formPage.id} tab={formPage.name} forceRender>
                    {(formPage?.formPageSections ?? []).map((formPageSection) => {
                      const numberOfLines = !isEmpty(formPageSection.formQuestions)
                        ? max(formPageSection.formQuestions.map((each) => each.line)) + 2
                        : 1

                      return (
                        <Section key={formPageSection.id} {...getSectionProps(formTemplate, formPageSection)}>
                          {[...Array(numberOfLines).keys()].map((line) => {
                            const formQuestions = orderBy(formPageSection?.formQuestions ?? [], [
                              'line',
                              'position',
                            ]).filter((each) => each.line === line)

                            return (
                              <Row key={line}>
                                {formQuestions.map((formQuestion) => {
                                  const formAnswer = (item?.formAnswers ?? []).find(
                                    (one) => one.questionId === formQuestion.id
                                  )

                                  console.assert(formAnswer, `no answer for questionId = ${formQuestion.id}`)

                                  const { fieldWidth, labelWidth, inputWidth } = getWidths({
                                    formTemplate,
                                    formQuestion,
                                  })
                                  const color = formPageSection.overrideFormColor
                                    ? formPageSection.contentForegroundColor
                                    : formTemplate.formForegroundColor
                                  const label = getLabel({ formQuestion, labelWidth, color })
                                  const focused = this.state.focusedId === formQuestion.id
                                  const hasError =
                                    !isValidAnswer(formQuestion, formAnswer) && !isEmpty(questionErrors)
                                  const setFocusId = (id) => this.setState({ focusedId: id })

                                  return (
                                    <Col key={formQuestion.id} xs={24} md={fieldWidth}>
                                      <Form.Item
                                        label={label}
                                        required={
                                          formQuestion.required &&
                                          allowRequired(formQuestion) &&
                                          !isEmpty(label?.props?.children)
                                        }
                                        labelCol={{
                                          xs: { span: 24 },
                                          lg: { span: labelWidth },
                                        }}
                                        wrapperCol={{
                                          xs: { span: 24 },
                                          lg: { span: inputWidth },
                                        }}
                                        onClick={() => setFocusId(formQuestion.id)}
                                        onMouseEnter={() => setFocusId(formQuestion.id)}
                                        onMouseLeave={() => setFocusId(null)}
                                        validateStatus={hasError ? 'error' : undefined}
                                        help={
                                          hasError ? (
                                            t('requiredField')
                                          ) : (
                                            <Actions>
                                              {!readOnly && focused && allowHighlight(formQuestion) && (
                                                <Tooltip title={t('highlightField')}>
                                                  <Button
                                                    type="link"
                                                    size="small"
                                                    onClick={() =>
                                                      this.setAnswerValue(formQuestion.id, {
                                                        highlight: !formAnswer?.highlight,
                                                      })
                                                    }
                                                  >
                                                    <Icon type="Highlight" color={color} />
                                                  </Button>
                                                </Tooltip>
                                              )}
                                              {!readOnly && focused && allowAttachment(formQuestion) && (
                                                <Tooltip title={t('uploadAttachment')}>
                                                  <Button
                                                    type="link"
                                                    size="small"
                                                    onClick={() =>
                                                      this.setState({
                                                        fileUploadQuestionId: formQuestion.id,
                                                        fileUploadDrawerVisible: true,
                                                      })
                                                    }
                                                  >
                                                    <Icon type="FileUpload" color={color} />
                                                  </Button>
                                                </Tooltip>
                                              )}
                                              {allowAttachment(formQuestion) &&
                                                formAnswer?.attachmentFileName && (
                                                  <Tooltip
                                                    title={`${t('download')} ${
                                                      formAnswer.attachmentFileName
                                                    }`}
                                                  >
                                                    <Button
                                                      type="link"
                                                      size="small"
                                                      onClick={() =>
                                                        this.downloadAttachment(
                                                          formQuestion.id,
                                                          formAnswer.attachmentFileName
                                                        )
                                                      }
                                                    >
                                                      <Icon type="GetApp" color={color} />
                                                    </Button>
                                                  </Tooltip>
                                                )}
                                              {!readOnly &&
                                                allowAttachment(formQuestion) &&
                                                formAnswer?.attachmentFileName && (
                                                  <Tooltip
                                                    title={`${t('delete')} ${formAnswer.attachmentFileName}`}
                                                  >
                                                    <Button
                                                      type="link"
                                                      size="small"
                                                      onClick={() => this.deleteAttachment(formQuestion.id)}
                                                    >
                                                      <Icon type="Delete" color={color} />
                                                    </Button>
                                                  </Tooltip>
                                                )}
                                            </Actions>
                                          )
                                        }
                                      >
                                        <Field
                                          question={formQuestion}
                                          answer={formAnswer}
                                          onChange={(value) =>
                                            this.setAnswerValue(formQuestion.id, { value })
                                          }
                                          layout={labelWidth === 24 ? 'vertical' : 'horizontal'}
                                          color={color}
                                          readOnly={readOnly}
                                        />
                                      </Form.Item>
                                    </Col>
                                  )
                                })}
                              </Row>
                            )
                          })}
                        </Section>
                      )
                    })}
                  </Tabs.TabPane>
                ))}
              </Tabs>
              {formTemplate.footer && <div className="mb-12">{formTemplate.footer}</div>}
            </Container>
          </div>
          <div className="form-buttons-container">
            <div className="float-right text-right">
              <FormButtons
                readOnly={readOnly}
                onCancel={this.props.onCancel}
                onSubmit={this.handleSubmit}
                saveButtonLoading={this.state.saveButtonLoading}
                saveAndCloseButtonLoading={this.state.saveAndCloseButtonLoading}
              />
            </div>
            {!readOnly && !item.done && item.id && (
              <div className="float-left text-left">
                <Button
                  type="primary"
                  onClick={this.handleSubmitButtonClick}
                  loading={this.state.submitButtonLoading}
                >
                  {t('submit')}
                </Button>
              </div>
            )}
          </div>
        </Form>
        <Drawer
          title={t('uploadAttachment')}
          size="xs"
          visible={this.state.fileUploadDrawerVisible}
          onClose={() => this.setState({ fileUploadDrawerVisible: false })}
        >
          <FileUpload
            onUpload={this.handleFileUpload}
            onCancel={() => this.setState({ fileUploadDrawerVisible: false })}
          />
        </Drawer>
        <Modal
          title={t('printPreview')}
          okText={t('print')}
          onOk={createPrintHandler(this.printableRef)}
          onCancel={() => this.setState({ printVisible: false }, resetPrintHandler)}
          width={992}
          visible={this.state.printVisible}
        >
          <div ref={this.printableRef} className="tofino-print-preview">
            <Print parentRecord={{ ...this.state.printItem }} />
          </div>
        </Modal>
        <Drawer
          title={this.state.sourceDrawerTitle}
          size={this.state.sourceDrawerSize}
          visible={this.state.sourceDrawerVisible}
          onClose={() => this.setState({ sourceDrawerVisible: false })}
        >
          <SourceComponent
            linkTargetRecord={this.state.sourceDrawerLinkTargetRecord}
            onCancel={() => this.setState({ sourceDrawerVisible: false })}
            readOnly
          />
        </Drawer>
      </>
    )
  }
}

export default flow(
  withRouter,
  Form.create({
    onValuesChange: (...params) => Emitter.emit(FORM_CHANGE_EVENT, ...params),
  })
)(FormView)
